/*
 * tg-adult-vsl
 *
 * Two jobs, both scoped to this section:
 *
 *   1. Play the VSL. Muted autoplay while the card is on screen, with one tap
 *      to bring in sound. Muted autoplay is the only kind browsers allow
 *      without a gesture, and it is worth having here because the source has
 *      burned-in captions, so a scroller who never taps still gets the message.
 *   2. Scroll the CTA to the buy box with an offset.
 *
 * The scroll half is deliberately a copy of assets/tg-kids-cta.js rather than a
 * shared import. Same reason that file gives for measuring the sticky bars at
 * runtime instead of setting `#product--section { scroll-margin-top }`:
 * #product--section is owned by sections/ecom-product.liquid and shared with
 * the live theme, so nothing outside this section gets touched. Duplicating
 * ~40 lines is the cheaper trade against introducing a shared JS file that two
 * sections on two templates would both depend on.
 *
 * Custom element rather than an IIFE so connectedCallback re-runs after a
 * Shopify theme-editor section reload.
 *
 * Fails safe: with JS off or broken, the <video> keeps its native `controls`
 * attribute and the CTA href is a real anchor, so both still work.
 */
if (!customElements.get("tg-adult-vsl")) {
  customElements.define(
    "tg-adult-vsl",
    class TgAdultVsl extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === "true") return;
        this.dataset.ready = "true";

        // The MP4-asset branch carries the data attribute. The Files branch is
        // rendered by Liquid's video_tag, which does not pass arbitrary
        // attributes through, so the bare tag name has to be matched too.
        this.video = this.querySelector("[data-tgavsl-video], video");
        this.overlay = this.querySelector("[data-tgavsl-overlay]");
        this.promptText = this.querySelector("[data-tgavsl-prompt-text]");
        this.link = this.querySelector("[data-tgavsl-scroll]");

        this.onScrollClick = this.onScrollClick.bind(this);
        this.onOverlayClick = this.onOverlayClick.bind(this);

        if (this.link) this.link.addEventListener("click", this.onScrollClick);

        // An embedded provider (YouTube/Vimeo) renders an iframe instead, and
        // owns its own controls. Only the scroll half applies there.
        if (this.video) this.setupVideo();
      }

      disconnectedCallback() {
        if (this.link) this.link.removeEventListener("click", this.onScrollClick);
        if (this.overlay) this.overlay.removeEventListener("click", this.onOverlayClick);
        if (this.observer) this.observer.disconnect();
      }

      /* ------------------------- video ------------------------- */

      setupVideo() {
        this.soundOn = false;

        // Native controls are on in the markup so the video is usable with JS
        // off. With JS on they are removed until the shopper takes over, so the
        // control bar does not sit on top of the sound prompt during autoplay.
        this.video.controls = false;
        this.video.muted = true;
        this.video.playsInline = true;

        if (this.overlay) this.overlay.addEventListener("click", this.onOverlayClick);

        var reduce =
          window.matchMedia &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this.autoplay = this.dataset.autoplay === "true" && !reduce;

        if (!this.autoplay) {
          this.setIdle();
          return;
        }

        this.setPlayingMuted();
        this.observe();
      }

      /*
       * Autoplay only while the card is at least half on screen, and pause when
       * it leaves. Playing a 9:16 video the whole way down a long PDP burns
       * mobile data for no benefit, and an unmuted one would keep talking after
       * the shopper has scrolled past it.
       */
      observe() {
        if (!("IntersectionObserver" in window)) {
          this.tryPlay();
          return;
        }

        var self = this;
        this.observer = new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) self.tryPlay();
              else self.pause();
            }
          },
          { threshold: 0.5 },
        );

        this.observer.observe(this);
      }

      tryPlay() {
        // Once the shopper has paused deliberately, scrolling back must not
        // restart it under them.
        if (this.userPaused) return;

        var p = this.video.play();

        // Safari's low-power mode and iOS data saver both reject muted
        // autoplay. Fall back to the idle poster and play badge.
        if (p && typeof p.catch === "function") {
          var self = this;
          p.catch(function () {
            self.setIdle();
            if (self.observer) self.observer.disconnect();
          });
        }
      }

      pause() {
        if (!this.video.paused) this.video.pause();
      }

      setIdle() {
        this.classList.add("tg-adult-vsl--idle");
        if (this.promptText) this.promptText.textContent = this.dataset.labelPlay || "Play video";
        if (this.overlay) {
          this.overlay.hidden = false;
          this.overlay.setAttribute("aria-label", this.dataset.labelPlay || "Play video");
        }
      }

      setPlayingMuted() {
        this.classList.remove("tg-adult-vsl--idle");
        if (this.promptText) this.promptText.textContent = this.dataset.labelSound || "Tap for sound";
        if (this.overlay) {
          this.overlay.hidden = false;
          this.overlay.setAttribute("aria-label", this.dataset.labelSound || "Tap for sound");
        }
      }

      /*
       * One handler for both overlay states, because both end in the same
       * place: sound on, native controls handed over, overlay gone.
       */
      onOverlayClick() {
        this.userPaused = false;
        this.soundOn = true;

        this.video.muted = false;
        this.video.volume = 1;

        // Restart from zero the first time sound comes on. This is a sales
        // video: someone who taps at 0:30 has heard none of it, and the opening
        // is the part that has to land. Opt out with "Restart when sound is
        // turned on" in the theme editor.
        if (this.dataset.restartOnSound === "true" && this.video.currentTime > 0.4) {
          this.video.currentTime = 0;
        }

        this.classList.remove("tg-adult-vsl--idle");
        if (this.overlay) this.overlay.hidden = true;
        this.video.controls = true;

        var self = this;
        var p = this.video.play();
        if (p && typeof p.catch === "function") {
          p.catch(function () {
            // Unmuted playback was refused. Put the poster state back rather
            // than leaving a dead frame with no way to start it.
            self.video.muted = true;
            self.setIdle();
          });
        }

        // From here the shopper owns playback, so the observer must stop
        // pausing and resuming underneath them.
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        this.video.addEventListener(
          "pause",
          function () {
            self.userPaused = true;
          },
          { once: true },
        );
      }

      /* ------------------------- scroll ------------------------- */

      /*
       * Sum of anything pinned to the top of the viewport. Elements are only
       * counted when they are actually at the top (<= 4px) and are plausibly a
       * bar rather than a full-screen overlay (< 200px tall), so a sticky
       * sidebar or an open modal does not inflate the offset.
       */
      stickyOffset() {
        var max = 0;
        var nodes = document.body.querySelectorAll("*");

        for (var i = 0; i < nodes.length; i++) {
          var el = nodes[i];
          var pos = getComputedStyle(el).position;
          if (pos !== "fixed" && pos !== "sticky") continue;

          var r = el.getBoundingClientRect();
          if (r.height <= 0 || r.height >= 200) continue;
          if (r.top > 4 || r.bottom <= 0) continue;
          if (r.bottom > max) max = r.bottom;
        }

        return max;
      }

      onScrollClick(event) {
        var selector = this.link.getAttribute("href");
        if (!selector || selector.charAt(0) !== "#") return;

        var target = document.querySelector(selector);
        if (!target) return; // let the browser handle it / do nothing

        event.preventDefault();

        var extra = parseInt(this.dataset.scrollExtra, 10);
        if (isNaN(extra)) extra = 16;

        var top =
          target.getBoundingClientRect().top +
          window.pageYOffset -
          this.stickyOffset() -
          extra;

        if (top < 0) top = 0;

        var reduce =
          window.matchMedia &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        window.scrollTo({ top: top, behavior: reduce ? "auto" : "smooth" });

        // Sound keeps playing while the shopper reads the buy box otherwise,
        // which reads as a page that will not shut up.
        if (this.video && this.soundOn && this.dataset.pauseOnCta === "true") {
          this.video.pause();
          this.userPaused = true;
        }
      }
    },
  );
}
